import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../api";

/**
 * Threads listed for the active environment across ALL agents the user can see.
 * `agentId` is used only when CREATING a new thread (each thread is bound to one agent).
 *
 * The list refreshes:
 *  - On mount + env change (immediate)
 *  - When useUserThreadStatus pushes a thread.status change (live)
 *  - Every 20s as a safety net so a missed status push (e.g. dropped user-status
 *    WS, backend restart) eventually clears stale "Responding…" indicators.
 *    Polling pauses when the document is hidden so background tabs stay quiet.
 */
const POLL_INTERVAL_MS = 20_000;

export function useAgentThreads(agentId, environmentId) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const refreshRef = useRef(null);
  // Thread ids with an in-flight optimistic delete. We filter these out of any
  // refresh so a 20s poll / status push that lands mid-delete can't briefly
  // re-add a row the user already saw vanish.
  const pendingDeletesRef = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!environmentId) { setThreads([]); return; }
    setLoading(true);
    try {
      const list = await api.listThreads({ environmentId });
      const pending = pendingDeletesRef.current;
      setThreads(pending.size ? list.filter((t) => !pending.has(t.thread_id)) : list);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [environmentId]);

  refreshRef.current = refresh;

  useEffect(() => { refresh(); }, [refresh]);

  // Polling fallback. We use refreshRef so the interval doesn't tear down +
  // recreate every time `refresh` changes identity.
  useEffect(() => {
    if (!environmentId) return undefined;
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      refreshRef.current?.();
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") refreshRef.current?.(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [environmentId]);

  const createThread = useCallback(async (opts = {}) => {
    // agentId may be null — backend falls back to the system default agent.
    // model is forwarded so the backend can route to the matching default-
    // agent variant. Admin agents ignore the model param.
    const t = await api.createThread(
      agentId || null,
      null,
      environmentId,
      opts.model || null,
      !!opts.requireApproval,
    );
    await refresh();
    return t;
  }, [agentId, environmentId, refresh]);

  const renameThread = useCallback(async (id, title) => {
    await api.patchThread(id, { title });
    await refresh();
  }, [refresh]);

  const deleteThread = useCallback(async (id) => {
    // Optimistic: drop the row immediately so the action feels instant, then
    // delete on the backend in the background. On failure, pull the truth back
    // (the thread reappears) and surface the error to the caller.
    pendingDeletesRef.current.add(id);
    setThreads((prev) => prev.filter((t) => t.thread_id !== id));
    try {
      await api.deleteThread(id);
    } catch (e) {
      pendingDeletesRef.current.delete(id);
      refresh();
      throw e;
    }
    pendingDeletesRef.current.delete(id);
  }, [refresh]);

  return { threads, loading, error, refresh, createThread, renameThread, deleteThread };
}
